let cachedVapidKey: string | null = null;

// VAPID public keys are safe to ship to the browser. Keeping the production
// key as a fallback prevents a missing hosting-provider build variable from
// silently disabling Web Push; VITE_VAPID_PUBLIC_KEY still takes precedence
// for staging or key rotation.
const DEFAULT_VAPID_PUBLIC_KEY =
  "BAFpe5nV6i3Sv1_NW2b4JDKrujqoKRnmN4D8lks6L96BSAh-m4y8t-1Kqqr78c991tfSsg1JtZz1kderfp94aww";

export async function getVapidPublicKey(): Promise<string> {
  if (cachedVapidKey) return cachedVapidKey;
  // The VAPID public key is exposed at build time via env var
  const key =
    (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined)?.trim() ||
    DEFAULT_VAPID_PUBLIC_KEY;
  cachedVapidKey = key;
  return key;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    return await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
  } catch (err) {
    console.warn("Service worker registration failed:", err);
    return null;
  }
}

export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return null;
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) return existing;
    const vapidKey = await getVapidPublicKey();

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    });

    return subscription;
  } catch (err) {
    console.warn("Push subscription failed:", err);
    return null;
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Convert base64 VAPID key to Uint8Array for push manager
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Serialize PushSubscription to a string for storage in Convex
export function serializeSubscription(sub: PushSubscription): string {
  return JSON.stringify(sub.toJSON());
}
